import { Card, CardContent, Collapse, Typography, Box, Stack, Chip } from '@mui/material'
import { format } from 'date-fns'
import type React from 'react'
import { useContext, useState } from 'react'

import {
  getOpenBankingOptOutReasonText,
  getPayrollOptOutReasonText,
} from '@/components/IncomeCard/utils'
import CancelOpenBankingRequest from '@/components/Requests/CancelRequest'
import type { RequestCard as Props } from '@/components/Requests/types'
import { getStatusProps } from '@/components/Requests/utils'
import ctx from '@/context/AppContext'
import {
  RequestType,
  isOpenBankingRecommendation,
  isPayrollRecommendation,
} from '@/types/Recommendation'

import RequestCardTimeline from './RequestCardTimeline'

export const RequestCard: React.FC<Props> = ({
  recommendation,
  activities,
  type,
  customerId,
  applicationId,
  subjectId,
  onRequestChanged,
}) => {
  const { personId } = useContext(ctx)
  const [expanded, setExpanded] = useState(false)
  const statusChipProps = getStatusProps(
    recommendation?.status ?? (recommendation.completed ? 'Complete' : 'Active'),
  )

  const isOpenBanking = isOpenBankingRecommendation(recommendation)
  const isPayroll = isPayrollRecommendation(recommendation)

  const requestTypeText = isPayroll ? 'Payroll/HMRC' : 'Open Banking'
  const requestType = isPayroll ? RequestType.PAYROLL : RequestType.OPEN_BANKING

  const toggleExpanded = () => setExpanded(prev => !prev)

  const shouldShowAdditionalDetails =
    recommendation.optOutReason?.category === 'somethingElse' &&
    !!recommendation.optOutReason?.additionalInformation

  return (
    <Card
      variant="outlined"
      sx={{
        mb: 2,
        bgcolor: '#FBF9F7',
        borderColor: '#EEEEEE',
        borderRadius: '8px',
        overflow: 'hidden',
      }}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Stack spacing={2}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography
              component="h4"
              sx={{ fontSize: '16px', fontWeight: 700, color: '#29222a' }}
            >
              {type === 'completed' ? 'Request Completed' : 'Active Request'}
            </Typography>
            <Chip
              {...statusChipProps}
              data-testid="status-chip"
              sx={[
                {
                  borderRadius: '5px',
                  fontSize: '14px',
                  '& .MuiChip-label': {
                    padding: '5px 10px',
                  },
                },
                ...(Array.isArray(statusChipProps.sx)
                  ? statusChipProps.sx
                  : [statusChipProps.sx]),
              ]}
            />
          </Stack>

          {recommendation.optOutReason && (
            <Stack direction="row" spacing={2}>
              <Box flex={1}>
                <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#29222a' }}>
                  Selected reason
                </Typography>
                <Typography sx={{ fontSize: '14px', color: '#534e54' }}>
                  {isOpenBanking
                    ? getOpenBankingOptOutReasonText(recommendation.optOutReason.category)
                    : getPayrollOptOutReasonText(recommendation.optOutReason.category)}
                </Typography>
              </Box>
              {shouldShowAdditionalDetails && (
                <Box flex={1}>
                  <Typography
                    sx={{ fontSize: '14px', fontWeight: 600, color: '#29222a' }}
                  >
                    Additional details
                  </Typography>
                  <Typography sx={{ fontSize: '14px', color: '#534e54' }}>
                    {recommendation.optOutReason.additionalInformation}
                  </Typography>
                </Box>
              )}
            </Stack>
          )}

          <Typography
            sx={{ fontSize: '12px', color: '#534e54', letterSpacing: '0.12px' }}
          >
            {(isOpenBanking || isPayroll
              ? `${requestTypeText} chaser sent on `
              : 'Request sent on ') +
              (recommendation.createdAt
                ? format(new Date(recommendation.createdAt), "dd/MM/yyyy 'at' HH:mm")
                : 'Unknown date')}{' '}
            by{' '}
            <Box component="span" sx={{ fontWeight: 600, color: '#6c0049' }}>
              {recommendation.author ?? 'Unknown user'}
            </Box>
          </Typography>

          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Box sx={{ width: '100%', borderTop: '1px solid #666' }} />

              <RequestCardTimeline
                recommendation={recommendation}
                activities={activities}
                type={type}
                requestTypeText={requestTypeText}
              />
            </Stack>
          </Collapse>

          <Box sx={{ textAlign: 'center' }}>
            <Typography
              component="button"
              type="button"
              onClick={toggleExpanded}
              sx={{
                appearance: 'none',
                border: 'none',
                background: 'transparent',
                p: 0,
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 500,
                color: '#1b4554',
                textDecoration: 'underline',
                letterSpacing: '0.14px',
                '&:hover': { textDecoration: 'none' },
              }}
            >
              {expanded ? 'Collapse' : 'Show more'}
            </Typography>
          </Box>

          {type === 'active' && !recommendation.completed && (
            <Box>
              <CancelOpenBankingRequest
                customerId={customerId}
                applicationId={applicationId}
                subjectId={subjectId}
                recommendationId={recommendation.id}
                requestType={requestType}
                onCancelled={onRequestChanged}
                userId={personId}
              />
            </Box>
          )}
        </Stack>
      </CardContent>
    </Card>
  )
}

export default RequestCard
